import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import {
	Alert,
	Badge,
	Button,
	Checkbox,
	Drawer,
	Input,
	Modal,
	message,
	Popconfirm,
	Select,
	Table,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import { ReloadOutlined, ExperimentOutlined, CaretRightOutlined, PlusOutlined } from '@ant-design/icons';
import {
	createSFTPProcessing,
	updateATSProcessing,
	updateSFTPProcessing,
} from 'src/_shared/api/graphql/custom/ats-processing';
import { SearchComponent } from 'src/_shared/components/search';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import { lambda } from 'src/_shared/services/utils.js';
import MappingEditorDrawer from './MappingEditorDrawer.jsx';
import AefMappingWizard from 'src/settings-dashboard/integrations/AefMappingWizard.jsx';
import {
	HEALTH_RANK,
	STALE_HOURS,
	aggregateIntegrations,
	deleteDlqMessage,
	fetchCompanyConfigMap,
	fetchDlqMessages,
	fetchFeedHistory,
	redriveDlqMessage,
	fetchIntegrationsHealthFast,
	fetchLedgerRuns,
	healthStats,
	isDryRun,
	runDate,
} from './integrationsData.js';
import { parseRunSummary } from './runSummary.js';

const { Option } = Select;
const { Text } = Typography;

// Deep-link params, set by the Admin Console health card: ?health=stale,
// ?activity=running, ?history=<integration key> (opens that feed's history
// drawer once the ledger loads). Read once at mount; the filters are
// page-local state after that.
const urlParam = (name, allowed) => {
	const value = new URLSearchParams(window.location.search).get(name);
	if (!value) return null;
	return !allowed || allowed.includes(value) ? value : null;
};

/**
 * Admin Integrations — superAdmin console for every customer integration.
 *
 * Reads the SFTPProcessingTable + ATSProcessingTable ledgers through the
 * existing AppSync status indexes and aggregates the latest run per
 * (source, company, syncType). Actions:
 *  - Run now: SFTP → writes a `retrigger-pending` request; the new-pipeline
 *    executor validates it references the company's LATEST file, then
 *    self-copies the S3 object so the classifier re-runs it live. ATS → flips
 *    the latest row back to pending (the ICIMS-Workday box still polls).
 *  - Dry run (SFTP): creates a `dryrun-pending` row that the new-pipeline
 *    executor Lambda claims, runs with writes SIMULATED, and completes with a
 *    report in `logs`. Legacy workers only poll status='pending', so dry-run
 *    rows are invisible to them.
 */

const STATUS_COLORS = {
	completed: 'green',
	completed_with_errors: 'volcano',
	failed: 'red',
	pending: 'gold',
	'retrigger-pending': 'orange',
	queued: 'geekblue',
	processing: 'blue',
	'dryrun-pending': 'purple',
	'dryrun-processing': 'purple',
	'dryrun-completed': 'cyan',
	'dryrun-failed': 'magenta',
};

// timeZoneName lets the browser print EST vs EDT for the actual date — a
// hardcoded "EST" label is wrong for two-thirds of the year.
const formatEastern = (value) => {
	if (!value) return '—';
	try {
		return new Date(value).toLocaleString('en-US', {
			timeZone: 'America/New_York',
			timeZoneName: 'short',
		});
	} catch (e) {
		return String(value);
	}
};

const formatAgo = (value) => {
	if (!value) return 'never';
	const ms = Date.now() - new Date(value).getTime();
	const minutes = Math.floor(ms / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 48) return `${hours}h ${minutes % 60}m ago`;
	return `${Math.floor(hours / 24)}d ago`;
};

const formatDuration = (ms) => {
	if (ms < 1000) return '<1s';
	const seconds = Math.floor(ms / 1000);
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
	return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

// Run duration: computed from start/end when both exist so legacy and
// new-pipeline rows read uniformly; legacy's processTime string is the
// fallback for rows that never got an endDate.
const runDuration = (run) => {
	const start = run.startDate && new Date(run.startDate).getTime();
	const end = run.endDate && new Date(run.endDate).getTime();
	if (start && end && end >= start) return formatDuration(end - start);
	return run.processTime || null;
};

const shortFile = (file) => {
	if (!file) return '—';
	const parts = String(file).split('/');
	return parts[parts.length - 1] || file;
};

/** The executor stores the dry-run report as JSON in `logs`. */
const parseDryRunReport = (run) => {
	try {
		const parsed = JSON.parse(run.logs);
		return parsed && parsed.dryRun ? parsed : null;
	} catch (e) {
		return null;
	}
};

/** Live runs store a structured report as `{v:1, runReport}` JSON in `logs`. */
const hasStructuredLogs = (run) => {
	try {
		return JSON.parse(run.logs)?.v === 1;
	} catch (e) {
		return false;
	}
};

/**
 * One-line, human-readable outcome of a run, kept deliberately quiet: counts
 * as muted prose ("45 claims created · 250 claims updated"), "no changes" for
 * steady state, a small ⚠ tooltip for breaker/truncation warnings, and the
 * error message for failed runs. The status tag next to it carries the color;
 * this line carries the words.
 */
/** Failure-kind labels for the classifier in runSummary.js — a glanceable
 *  "what bucket is this" tag so admins don't parse AWS prose to triage. */
const ERROR_KIND_META = {
	auth: { label: 'auth', color: 'red' },
	'missing-file': { label: 'file missing', color: 'gold' },
	pgp: { label: 'pgp', color: 'geekblue' },
	parse: { label: 'bad file', color: 'orange' },
	breaker: { label: 'breaker', color: 'magenta' },
	network: { label: 'network', color: 'volcano' },
	config: { label: 'config', color: 'purple' },
};

const RunSummaryLine = ({ run, compact, placeholder }) => {
	const summary = parseRunSummary(run);
	if (summary.error) {
		const kind = ERROR_KIND_META[summary.errorKind];
		return (
			<Tooltip title={summary.error}>
				<div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
					{kind ? (
						<Tag color={kind.color} style={{ fontSize: 11 }}>
							{kind.label}
						</Tag>
					) : null}
					<Text type="danger" style={{ fontSize: 12, maxWidth: 280, display: 'inline-block', verticalAlign: 'bottom' }} ellipsis>
						{summary.error}
					</Text>
				</div>
			</Tooltip>
		);
	}
	const showTotals = !compact && summary.totals.length > 0;
	const parts = summary.noChanges
		? ['no changes']
		: summary.chips.map((entry) => `${entry.value.toLocaleString()} ${entry.label}`);
	if (!parts.length && !summary.warnings.length && !showTotals) {
		return placeholder ? <Text type="secondary">—</Text> : null;
	}
	return (
		<div style={{ marginTop: 2 }}>
			{parts.length ? (
				<Text type="secondary" style={{ fontSize: 12 }}>
					{parts.join(' · ')}
				</Text>
			) : null}
			{summary.warnings.map((warning) => (
				<Tooltip key={warning} title={warning}>
					<Text style={{ fontSize: 12, marginLeft: 6, cursor: 'help' }}>⚠️</Text>
				</Tooltip>
			))}
			{showTotals ? (
				<div>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{summary.totals.join(' · ')}
					</Text>
				</div>
			) : null}
		</div>
	);
};

const AdminIntegrationsComponent = (props) => {
	const { client, currentUser } = props;
	const [runs, setRuns] = useState([]);
	// Rows from the lambda fast path; null → the client-side scan is in charge.
	const [serverIntegrations, setServerIntegrations] = useState(null);
	const [historyRuns, setHistoryRuns] = useState(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [dlqOpen, setDlqOpen] = useState(false);
	const [dlqMessages, setDlqMessages] = useState(null);
	const [loading, setLoading] = useState(false);
	const [lastRefreshed, setLastRefreshed] = useState(null);
	const [autoRefresh, setAutoRefresh] = useState(true);
	const [searchText, setSearchText] = useState('');
	const [sourceFilter, setSourceFilter] = useState('all');
	const [syncTypeFilter, setSyncTypeFilter] = useState('all');
	const [healthFilter, setHealthFilter] = useState(
		() => urlParam('health', ['failed', 'blocked', 'stale', 'healthy']) || 'all'
	);
	// Chip-only quick filter for in-flight work: null | 'running' | 'dryrun'.
	const [activityFilter, setActivityFilter] = useState(() =>
		urlParam('activity', ['running', 'dryrun'])
	);
	const [statusFilter, setStatusFilter] = useState('all');
	const [drawerKey, setDrawerKey] = useState(() => urlParam('history'));
	const [detailRun, setDetailRun] = useState(null);
	const [reportRun, setReportRun] = useState(null);
	const [mappingRow, setMappingRow] = useState(null);
	const [bypassRow, setBypassRow] = useState(null);
	const [bypassConfirmText, setBypassConfirmText] = useState('');
	const [wizardOpen, setWizardOpen] = useState(false);
	const [queueHealth, setQueueHealth] = useState(null);
	const [companyConfigs, setCompanyConfigs] = useState(null);
	const [showDisabled, setShowDisabled] = useState(false);
	const fetchingRef = useRef(false);

	// Every company's feed-enablement config, keyed by id. Fetched on mount and
	// on manual refresh only (configs change rarely; the auto-poll skips it).
	// Fail open: null means "unknown" and no feed gets hidden.
	const fetchCompanyConfigs = useCallback(async () => {
		try {
			setCompanyConfigs(await fetchCompanyConfigMap(client));
		} catch (error) {
			console.error('Error fetching company processing configs:', error);
		}
	}, [client]);

	// SQS depth per pipeline queue (+DLQ) via the mapping-config-api lambda.
	// Non-fatal by design: the ledger view must keep working when it errors.
	const fetchQueueHealth = useCallback(async () => {
		try {
			const res = await lambda({
				endpoint: 'mapping-config-api',
				variables: { action: 'queueHealth' },
			});
			const raw = res?.data ?? res;
			const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
			if (payload?.ok && Array.isArray(payload.queues)) setQueueHealth(payload);
		} catch (e) {
			// leave the previous reading in place
		}
	}, []);

	const refresh = useCallback(async () => {
		if (fetchingRef.current) return;
		fetchingRef.current = true;
		setLoading(true);
		try {
			// Fast path: one lambda call with the aggregation done in-region.
			// Falls back to the browser-side full-ledger scan (~25MB) when the
			// lambda doesn't have the action yet or errors.
			const fast = await fetchIntegrationsHealthFast();
			if (fast) {
				setServerIntegrations(fast.integrations);
			} else {
				setServerIntegrations(null);
				setRuns(await fetchLedgerRuns(client));
			}
			setLastRefreshed(new Date());
			fetchQueueHealth();
		} finally {
			fetchingRef.current = false;
			setLoading(false);
		}
	}, [client, fetchQueueHealth]);

	useEffect(() => {
		refresh();
		fetchCompanyConfigs();
	}, [refresh, fetchCompanyConfigs]);

	// ── Aggregate: one row per (source, companyId, syncType[, atsType]) ────────
	const integrations = useMemo(
		() => serverIntegrations ?? aggregateIntegrations(runs, companyConfigs),
		[serverIntegrations, runs, companyConfigs]
	);

	const hasActiveWork = useMemo(
		() => integrations.some((row) => row.active || row.dryRunActive),
		[integrations]
	);

	useEffect(() => {
		if (!autoRefresh) return undefined;
		const interval = setInterval(refresh, hasActiveWork ? 15000 : 60000);
		return () => clearInterval(interval);
	}, [autoRefresh, hasActiveWork, refresh]);

	const filtered = useMemo(() => {
		const needle = searchText.trim().toLowerCase();
		return integrations
			.filter((row) => showDisabled || row.health !== 'off')
			.filter((row) => sourceFilter === 'all' || row.source === sourceFilter)
			.filter((row) => syncTypeFilter === 'all' || row.syncType === syncTypeFilter)
			.filter((row) => healthFilter === 'all' || row.health === healthFilter)
			.filter((row) => {
				if (activityFilter === 'running') return row.active;
				if (activityFilter === 'dryrun') return row.dryRunActive;
				return true;
			})
			.filter(
				(row) => statusFilter === 'all' || row.latest?.status === statusFilter
			)
			.filter(
				(row) =>
					!needle ||
					(row.companyName || '').toLowerCase().includes(needle) ||
					(row.companyId || '').toLowerCase().includes(needle)
			)
			.sort((a, b) => {
				if (HEALTH_RANK[a.health] !== HEALTH_RANK[b.health])
					return HEALTH_RANK[a.health] - HEALTH_RANK[b.health];
				return (a.companyName || '').localeCompare(b.companyName || '');
			});
	}, [integrations, showDisabled, sourceFilter, syncTypeFilter, healthFilter, activityFilter, statusFilter, searchText]);

	const syncTypes = useMemo(
		() => [...new Set(integrations.map((row) => row.syncType).filter(Boolean))].sort(),
		[integrations]
	);

	const lastRunStatuses = useMemo(
		() =>
			[...new Set(integrations.map((row) => row.latest?.status).filter(Boolean))].sort(),
		[integrations]
	);

	const stats = useMemo(() => healthStats(integrations), [integrations]);

	// Company directory for the wizard's search — every company that appears in
	// the ledgers, by name. (A dedicated company list would be more complete; the
	// pilot company is injected as the default in the wizard regardless.)
	const companyOptions = useMemo(() => {
		const byId = new Map();
		integrations.forEach((row) => {
			if (row.companyId && !byId.has(row.companyId)) {
				byId.set(row.companyId, row.companyName || row.companyId);
			}
		});
		return [...byId.entries()]
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
	}, [integrations]);

	// Wizard dry-run: run the candidate doc against the company's latest landed
	// file. A brand-new company has no file yet — say so rather than fail silently.
	const wizardDryRun = ({ companyId: cid, taskType, doc }) => {
		const integration = integrations.find((r) => r.companyId === cid && r.syncType === taskType);
		const file =
			integration?.latest?.file ||
			integration?.latestDryRun?.file ||
			integration?.allRuns?.find((r) => r.file)?.file;
		if (!integration || !file) {
			message.warning(
				'No file has landed for this feed yet — save the config, then land a file (or run the pull) to dry-run against it.'
			);
			return;
		}
		triggerSftpRow(integration, 'dryrun-pending', 'Wizard dry run', doc);
	};

	// ── Actions ────────────────────────────────────────────────────────────────
	const requestedBy = `Requested from Admin Integrations by ${
		currentUser?.emailAddress || currentUser?.id || 'unknown'
	} at ${new Date().toISOString()}`;

	const triggerSftpRow = async (integration, status, label, candidateDoc, extra) => {
		const template =
			integration.latest || integration.latestDryRun || integration.allRuns?.[0];
		if (!template?.file) {
			message.error('No previous run with a file reference to replay.');
			return;
		}
		// Structured request payloads ride in `logs` as JSON: a candidate mapping
		// doc (executor runs it INSTEAD of the saved table doc — "test before
		// save"), and/or the one-shot breaker bypass (the retrigger sweep stamps
		// it into the S3 self-copy metadata for the worker). Plain requests keep
		// the bare requestedBy string.
		const payload = { ...(candidateDoc ? { candidateDoc } : {}), ...extra };
		try {
			await client.mutate({
				mutation: gql(createSFTPProcessing),
				variables: {
					// NOTE: CreateSFTPProcessingInput has no `id` — AppSync generates it.
					input: {
						companyId: template.companyId,
						companyName: template.companyName,
						file: template.file,
						syncType: template.syncType,
						status,
						createdDate: new Date().toISOString(),
						logs: Object.keys(payload).length
							? JSON.stringify({ ...payload, note: requestedBy })
							: requestedBy,
					},
				},
			});
			message.success(`${label} queued for ${template.companyName} ${template.syncType}.`);
			setTimeout(refresh, 1500);
		} catch (error) {
			console.error(`${label} failed:`, error);
			message.error(`${label} failed: ${error?.message || 'unknown error'}`);
		}
	};

	const runNow = async (integration) => {
		if (integration.source === 'ATS') {
			// ATS restart = flip the latest row back to pending (existing mechanism).
			const latest = integration.latest;
			if (!latest) {
				message.error('No previous ATS run to restart.');
				return;
			}
			try {
				await client.mutate({
					mutation: gql(updateATSProcessing),
					variables: { input: { id: latest.id, status: 'pending' } },
				});
				message.success(`Restart queued for ${integration.companyName}.`);
				setTimeout(refresh, 1500);
			} catch (error) {
				message.error(`Restart failed: ${error?.message || 'unknown error'}`);
			}
			return;
		}
		// SFTP: write a retrigger request; the executor validates it's the LATEST
		// dropped file, clears the idempotency record, and self-copies the object
		// so the classifier re-runs it through the live pipeline. (The old
		// behavior wrote status 'pending' for EC2 pollers that no longer exist.)
		await triggerSftpRow(integration, 'retrigger-pending', 'Run');
	};

	const dryRunNow = async (integration) =>
		triggerSftpRow(integration, 'dryrun-pending', 'Dry run');

	// What the breaker refused, for rows where a one-shot bypass makes sense:
	// SFTP feeds whose latest run was breaker-suppressed (employee disable
	// breaker — run 'completed' with writes suppressed) or breaker-failed (job
	// close breaker — run failed with the breaker error kind). Null otherwise.
	const breakerBlockReason = (row) => {
		if (row.source !== 'SFTP' || !row.latest) return null;
		const summary = parseRunSummary(row.latest);
		if (row.health === 'blocked') {
			return (
				summary.warnings.find((warning) => /writes suppressed by/i.test(warning)) ||
				'The disable breaker suppressed all writes on the last run.'
			);
		}
		if (row.latest.status === 'failed' && summary.errorKind === 'breaker') {
			return summary.error;
		}
		return null;
	};

	const confirmBypass = async () => {
		const row = bypassRow;
		setBypassRow(null);
		await triggerSftpRow(row, 'retrigger-pending', 'Breaker-bypass run', null, {
			bypassBreaker: true,
			requestedBy: currentUser?.emailAddress || currentUser?.id || 'unknown',
		});
	};

	const cancelRun = async (run) => {
		try {
			await client.mutate({
				mutation: gql(run.source === 'ATS' ? updateATSProcessing : updateSFTPProcessing),
				variables: {
					input: {
						id: run.id,
						status: isDryRun(run) ? 'dryrun-failed' : 'failed',
						endDate: new Date().toISOString(),
					},
				},
			});
			message.success('Run cancelled.');
			setTimeout(refresh, 1000);
		} catch (error) {
			message.error(`Cancel failed: ${error?.message || 'unknown error'}`);
		}
	};

	// ── Columns ────────────────────────────────────────────────────────────────
	const columns = [
		{
			title: 'Company',
			key: 'company',
			sorter: (a, b) =>
				(a.companyName || '').localeCompare(b.companyName || ''),
			render: (row) => (
				<div>
					<Text strong>{row.companyName}</Text>
					<div>
						<Tag>{row.source}</Tag>
						{row.atsType ? <Tag color="geekblue">{row.atsType}</Tag> : null}
					</div>
				</div>
			),
		},
		{
			title: 'Sync Type',
			dataIndex: 'syncType',
			key: 'syncType',
			sorter: (a, b) => (a.syncType || '').localeCompare(b.syncType || ''),
			render: (value) => <Tag color="blue">{value || '—'}</Tag>,
		},
		{
			title: 'Health',
			key: 'health',
			// Failing sorts first (ascending) so problems surface together.
			sorter: (a, b) =>
				(HEALTH_RANK[a.health] ?? 9) - (HEALTH_RANK[b.health] ?? 9),
			render: (row) => {
				if (row.health === 'off')
					return (
						<Tooltip title="This feed is off in the company's integration settings — the runs shown are historical.">
							<Badge status="default" text="Off" />
						</Tooltip>
					);
				if (row.health === 'failed') return <Badge status="error" text="Failing" />;
				if (row.health === 'blocked')
					return (
						<Tooltip title="The last run completed WITHOUT writing anything: the disable breaker suppressed all writes. Review what it wanted to do; if the disables are legitimate, re-run with a one-shot bypass.">
							<Badge status="error" text="Blocked (writes suppressed)" />
						</Tooltip>
					);
				if (row.health === 'stale')
					return <Badge status="warning" text={`Stale (no success in ${STALE_HOURS}h+)`} />;
				return <Badge status="success" text="Healthy" />;
			},
		},
		{
			title: 'Last Run',
			key: 'lastRun',
			// Never-ran rows sort as oldest so they surface together at one end.
			sorter: (a, b) =>
				(a.latest ? runDate(a.latest).getTime() : 0) -
				(b.latest ? runDate(b.latest).getTime() : 0),
			defaultSortOrder: 'descend',
			render: (row) => {
				const latest = row.latest;
				if (!latest) return <Text type="secondary">never</Text>;
				return (
					<Tooltip title={`${formatEastern(runDate(latest))} — ${shortFile(latest.file)}`}>
						<div>
							<div>
								<Tag color={STATUS_COLORS[latest.status] || 'default'}>{latest.status}</Tag>
								<Text type="secondary">{formatAgo(runDate(latest))}</Text>
								{runDuration(latest) ? (
									<Text type="secondary" style={{ fontSize: 12 }}>
										{' · duration '}
										{runDuration(latest)}
									</Text>
								) : null}
							</div>
							<RunSummaryLine compact run={latest} />
							{row.health === 'blocked' ? (
								<Text type="danger" style={{ fontSize: 12, display: 'block' }}>
									breaker suppressed all writes — nothing was written this run
								</Text>
							) : null}
							{row.health === 'stale' ? (
								<Text type="warning" style={{ fontSize: 12, display: 'block' }}>
									{`expected ${row.cadenceLabel || 'regular'} delivery · last success ${
										row.latestCompletedDate ? formatAgo(row.latestCompletedDate) : 'never'
									} — check customer delivery`}
								</Text>
							) : null}
						</div>
					</Tooltip>
				);
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (row) => (
				<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
					<Button size="small" onClick={() => setDrawerKey(row.key)}>
						History
					</Button>
					{row.source === 'CLAUS' ? (
						<Tooltip title="CLAUS runs are scheduled by the claus platform — no manual retrigger from this page.">
							<Button size="small" disabled icon={<CaretRightOutlined />}>
								Run now
							</Button>
						</Tooltip>
					) : (
						<Popconfirm
							title={
								row.source === 'SFTP'
									? `Re-run the latest ${row.syncType} file for ${row.companyName}? This will write data.`
									: `Queue a REAL ${row.syncType} run for ${row.companyName}? This will write data.`
							}
							okText="Run"
							onConfirm={() => runNow(row)}
						>
							<Button size="small" type="primary" icon={<CaretRightOutlined />} disabled={row.active}>
								{row.active ? 'Running…' : 'Run now'}
							</Button>
						</Popconfirm>
					)}
					{row.source === 'SFTP' ? (
						<Popconfirm
							title={`Dry run ${row.syncType} for ${row.companyName}? Reads are real, writes are simulated.`}
							okText="Dry run"
							onConfirm={() => dryRunNow(row)}
						>
							<Button size="small" icon={<ExperimentOutlined />} disabled={row.dryRunActive}>
								{row.dryRunActive ? 'Dry-running…' : 'Dry run'}
							</Button>
						</Popconfirm>
					) : null}
					{row.source === 'SFTP' ? (
						<Button size="small" onClick={() => setMappingRow(row)}>
							Mappings
						</Button>
					) : null}
					{breakerBlockReason(row) ? (
						<Button
							danger
							size="small"
							disabled={row.active}
							onClick={() => {
								setBypassConfirmText('');
								setBypassRow(row);
							}}
						>
							Bypass breaker
						</Button>
					) : null}
				</div>
			),
		},
	];

	// Headline stats double as quick filters: clicking a chip narrows the table
	// to those rows, clicking it again clears it. The active chip gets a ring.
	// Chip selection is mutually exclusive — activating one clears the other
	// kind, so the table always reflects exactly the chip you clicked.
	const toggleHealthChip = (value) => {
		setHealthFilter((current) => (current === value ? 'all' : value));
		setActivityFilter(null);
	};
	const toggleActivityChip = (value) => {
		setActivityFilter((current) => (current === value ? null : value));
		setHealthFilter('all');
	};
	const resetQuickFilters = () => {
		setHealthFilter('all');
		setActivityFilter(null);
		setStatusFilter('all');
	};

	const statChip = ({ label, type, active, onClick, showIcon = true }) => (
		<span
			key={label}
			role="button"
			tabIndex={0}
			title={active ? 'Click to clear this filter' : 'Click to filter the table'}
			style={{
				cursor: 'pointer',
				borderRadius: 2,
				outline: active ? '2px solid #1890ff' : 'none',
				outlineOffset: 1,
			}}
			onClick={onClick}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onClick();
				}
			}}
		>
			<Alert message={label} type={type} showIcon={showIcon} />
		</span>
	);

	const drawerIntegration = integrations.find((row) => row.key === drawerKey);

	// History drawer data: fallback-mode rows carry allRuns in memory; fast-mode
	// rows don't, so the drawer fetches that one feed's history on open.
	useEffect(() => {
		if (!drawerIntegration) {
			setHistoryRuns(null);
			return undefined;
		}
		if (drawerIntegration.allRuns) {
			setHistoryRuns(drawerIntegration.allRuns);
			return undefined;
		}
		let cancelled = false;
		setHistoryLoading(true);
		fetchFeedHistory(drawerIntegration)
			.then((rows) => {
				if (!cancelled) setHistoryRuns(rows);
			})
			.catch((error) => {
				if (!cancelled) {
					setHistoryRuns([]);
					message.error(`Couldn't load run history: ${error?.message || 'unknown error'}`);
				}
			})
			.finally(() => {
				if (!cancelled) setHistoryLoading(false);
			});
		return () => {
			cancelled = true;
		};
		// Refetching on every auto-refresh would churn the lambda; once per open.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drawerKey, Boolean(drawerIntegration)]);

	const openDlq = async () => {
		setDlqOpen(true);
		setDlqMessages(null);
		try {
			setDlqMessages(await fetchDlqMessages());
		} catch (error) {
			setDlqMessages([]);
			message.error(`Couldn't peek the DLQs: ${error?.message || 'unknown error'}`);
		}
	};

	// Retry (redrive to the main queue) or remove one dead-letter message,
	// then re-peek so the list and the strip's count stay honest.
	const refreshDlqList = async () => {
		try {
			setDlqMessages(await fetchDlqMessages());
		} catch (e) {
			// keep the stale list rather than blank the modal
		}
		fetchQueueHealth();
	};

	// On success, drop the row locally rather than re-peeking immediately — an
	// instant re-peek races SQS visibility/consistency and can read "empty"
	// while the depth chip still counts the rest.
	const removeDlqEntryLocally = (entry) => {
		setDlqMessages((current) =>
			(current || []).filter((item) => item.ledgerId !== entry.ledgerId)
		);
		fetchQueueHealth();
	};

	const actOnDlqEntry = async (entry, act, label) => {
		try {
			await act(entry);
			message.success(`${label} ${shortFile(entry.key)}.`);
			removeDlqEntryLocally(entry);
		} catch (error) {
			message.error(`${label} failed: ${error?.message || 'unknown error'}`);
			refreshDlqList();
		}
	};

	const retryDlqEntry = async (entry) => {
		try {
			await redriveDlqMessage(entry);
			message.success(`Retry queued for ${shortFile(entry.key)}.`);
			removeDlqEntryLocally(entry);
		} catch (error) {
			if (error?.requiresForce) {
				// The lambda refused: this retry would apply an OLDER file and the
				// stale-replay guard would NOT stop it. Escalate to an explicit
				// danger confirmation before forcing.
				Modal.confirm({
					title: 'Retry would run an OLDER file',
					okText: 'Run older file anyway',
					okButtonProps: { danger: true },
					width: 520,
					content: (
						<div>
							<p>{error.message}</p>
							<p style={{ marginBottom: 4 }}>
								This message: <Text code>{shortFile(error.thisFile || entry.key)}</Text>
							</p>
							{error.latestFile ? (
								<p style={{ marginBottom: 4 }}>
									Latest delivery: <Text code>{shortFile(error.latestFile)}</Text>
									{error.latestDate ? (
										<Text type="secondary"> ({formatAgo(error.latestDate)})</Text>
									) : null}
								</p>
							) : null}
							<p>
								Retrying the latest failed delivery first (or “Run now” on the feed)
								is usually the right move.
							</p>
						</div>
					),
					onOk: () =>
						actOnDlqEntry(
							entry,
							(target) => redriveDlqMessage(target, { force: true }),
							'Retry queued for'
						),
				});
				return;
			}
			message.error(`Retry failed: ${error?.message || 'unknown error'}`);
			refreshDlqList();
		}
	};

	// Fixed layout + explicit widths: a run result can contain arbitrarily long
	// unbreakable tokens (IAM ARNs, S3 keys) and must clip inside its cell
	// rather than widen the table and push the action buttons out of the drawer.
	const historyColumns = [
		{
			title: 'Started',
			key: 'started',
			width: 100,
			render: (run) => (
				<Tooltip title={formatEastern(runDate(run))}>{formatAgo(runDate(run))}</Tooltip>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 130,
			render: (value) => <Tag color={STATUS_COLORS[value] || 'default'}>{value}</Tag>,
		},
		{
			title: 'Result',
			key: 'result',
			render: (run) => (
				<div style={{ overflow: 'hidden' }}>
					<RunSummaryLine compact placeholder run={run} />
				</div>
			),
		},
		{
			title: 'Duration',
			key: 'duration',
			width: 90,
			render: (run) => runDuration(run) || '—',
		},
		{
			title: 'File',
			key: 'file',
			width: 180,
			render: (run) => (
				<Tooltip title={run.file}>
					<Text style={{ maxWidth: 160 }} ellipsis>
						{shortFile(run.file)}
					</Text>
				</Tooltip>
			),
		},
		{
			title: '',
			key: 'runActions',
			width: 160,
			render: (run) => (
				<div style={{ display: 'flex', gap: 8 }}>
					<Button size="small" onClick={() => setDetailRun(run)}>
						Details
					</Button>
					{run.status === 'dryrun-completed' && parseDryRunReport(run) ? (
						<Button size="small" type="link" onClick={() => setReportRun(run)}>
							Report
						</Button>
					) : null}
					{['pending', 'processing', 'dryrun-pending', 'dryrun-processing'].includes(
						run.status
					) ? (
						<Popconfirm title="Mark this run as failed/cancelled?" onConfirm={() => cancelRun(run)}>
							<Button size="small" danger>
								Cancel
							</Button>
						</Popconfirm>
					) : null}
				</div>
			),
		},
	];

	const report = reportRun ? parseDryRunReport(reportRun) : null;

	return (
		<main style={{ padding: 24 }}>
			<div
				className="page-title"
				style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
			>
				<h2 className="page-heading">Admin Integrations</h2>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => setWizardOpen(true)}>
					New employee feed
				</Button>
			</div>

			<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
				{statChip({
					label: `${stats.total} integrations`,
					type: 'info',
					active: false,
					onClick: resetQuickFilters,
					showIcon: false,
				})}
				{statChip({
					label: `${stats.failed} failing`,
					type: stats.failed ? 'error' : 'success',
					active: healthFilter === 'failed',
					onClick: () => toggleHealthChip('failed'),
				})}
				{statChip({
					label: `${stats.blocked} blocked by breaker`,
					type: stats.blocked ? 'error' : 'success',
					active: healthFilter === 'blocked',
					onClick: () => toggleHealthChip('blocked'),
				})}
				{statChip({
					label: `${stats.stale} stale`,
					type: stats.stale ? 'warning' : 'success',
					active: healthFilter === 'stale',
					onClick: () => toggleHealthChip('stale'),
				})}
				{statChip({
					label: `${stats.running} running now`,
					type: 'info',
					active: activityFilter === 'running',
					onClick: () => toggleActivityChip('running'),
				})}
				{statChip({
					label: `${stats.dryRunning} dry runs in flight`,
					type: 'info',
					active: activityFilter === 'dryrun',
					onClick: () => toggleActivityChip('dryrun'),
				})}
				{queueHealth ? (
					<Tooltip
						title={queueHealth.queues
							.map(
								(q) =>
									`${q.taskType}: ${q.queued} queued · ${q.inFlight} in flight · ${q.dlq} dead-letter`
							)
							.join(' | ')}
					>
						<span style={{ display: 'flex', gap: 16 }}>
							<Alert
								message={`${queueHealth.queues.reduce((n, q) => n + q.queued + q.inFlight, 0)} in queues`}
								type="info"
								showIcon={false}
							/>
							<span
								role="button"
								tabIndex={0}
								title="Click to see which runs are stuck"
								style={{ cursor: 'pointer' }}
								onClick={openDlq}
								onKeyDown={(event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										openDlq();
									}
								}}
							>
								<Alert
									message={
										queueHealth.queues.reduce((n, q) => n + q.dlq, 0)
											? `${queueHealth.queues.reduce((n, q) => n + q.dlq, 0)} dead-letter — runs stuck after retries`
											: 'DLQ empty'
									}
									type={queueHealth.queues.reduce((n, q) => n + q.dlq, 0) ? 'error' : 'success'}
									showIcon
								/>
							</span>
						</span>
					</Tooltip>
				) : null}
			</div>

			<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
				{/* The app-wide pill search (same as Browse Jobs) — Input.Search's
				    hover-only clear X read as broken since it vanished on click. */}
				<div className="admin-integrations-search" style={{ width: 260 }}>
					<SearchComponent
						placeholder="Search company…"
						searchQuery={searchText}
						setQueryToState={setSearchText}
					/>
				</div>
				<Select value={sourceFilter} style={{ width: 160 }} onChange={setSourceFilter}>
					<Option value="all">All sources</Option>
					<Option value="SFTP">SFTP</Option>
					<Option value="ATS">ATS (iCIMS/WD)</Option>
					<Option value="CLAUS">CLAUS (platform)</Option>
				</Select>
				<Select value={syncTypeFilter} style={{ width: 150 }} onChange={setSyncTypeFilter}>
					<Option value="all">All sync types</Option>
					{syncTypes.map((type) => (
						<Option key={type} value={type}>
							{type}
						</Option>
					))}
				</Select>
				<Select value={healthFilter} style={{ width: 140 }} onChange={setHealthFilter}>
					<Option value="all">All health</Option>
					<Option value="failed">Failing</Option>
					<Option value="blocked">Blocked (breaker)</Option>
					<Option value="stale">Stale</Option>
					<Option value="healthy">Healthy</Option>
					{showDisabled ? <Option value="off">Off (disabled)</Option> : null}
				</Select>
				<Select value={statusFilter} style={{ width: 180 }} onChange={setStatusFilter}>
					<Option value="all">All run statuses</Option>
					{lastRunStatuses.map((status) => (
						<Option key={status} value={status}>
							<Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>
						</Option>
					))}
				</Select>
				{companyConfigs && stats.off ? (
					<Checkbox
						checked={showDisabled}
						style={{ alignSelf: 'center' }}
						onChange={(event) => {
							setShowDisabled(event.target.checked);
							// Hiding disabled feeds while filtered to Off would strand an
							// empty table on a value the dropdown no longer offers.
							if (!event.target.checked) {
								setHealthFilter((current) => (current === 'off' ? 'all' : current));
							}
						}}
					>
						Show disabled feeds ({stats.off})
					</Checkbox>
				) : null}
				<Button
					icon={<ReloadOutlined />}
					loading={loading}
					onClick={() => {
						fetchCompanyConfigs();
						refresh();
					}}
				>
					Refresh
				</Button>
				<Button type={autoRefresh ? 'primary' : 'default'} onClick={() => setAutoRefresh(!autoRefresh)}>
					Auto-refresh {autoRefresh ? 'on' : 'off'}
				</Button>
				{lastRefreshed ? (
					<Text type="secondary" style={{ alignSelf: 'center' }}>
						Updated {formatAgo(lastRefreshed)}
					</Text>
				) : null}
			</div>

			{loading && integrations.length === 0 ? (
				<Spinner />
			) : (
				<Table
					rowKey="key"
					columns={columns}
					dataSource={filtered}
					pagination={{ pageSize: 20, showSizeChanger: true }}
					size="middle"
				/>
			)}

			<Drawer
				title={
					drawerIntegration
						? `${drawerIntegration.companyName} — ${drawerIntegration.syncType} (${drawerIntegration.source})`
						: ''
				}
				width={1000}
				visible={Boolean(drawerIntegration)}
				onClose={() => setDrawerKey(null)}
			>
				{drawerIntegration ? (
					<Table
						rowKey="id"
						columns={historyColumns}
						dataSource={drawerIntegration.allRuns ?? historyRuns ?? []}
						loading={historyLoading}
						pagination={{ pageSize: 15 }}
						size="small"
						tableLayout="fixed"
					/>
				) : null}
			</Drawer>

			<Modal
				title={detailRun ? `${detailRun.companyName} — ${detailRun.syncType} run` : ''}
				visible={Boolean(detailRun)}
				width={760}
				footer={<Button onClick={() => setDetailRun(null)}>Close</Button>}
				onCancel={() => setDetailRun(null)}
			>
				{detailRun ? (
					<div>
						<p style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
							<Badge color={STATUS_COLORS[detailRun.status] || 'default'} text={detailRun.status} />
							<Text type="secondary">
								{formatEastern(runDate(detailRun))}
								{runDuration(detailRun) ? ` · ${runDuration(detailRun)}` : ''}
							</Text>
						</p>
						<p style={{ marginBottom: 12 }}>
							<Text type="secondary" style={{ fontSize: 12 }}>
								{detailRun.file}
							</Text>
						</p>
						{parseRunSummary(detailRun).warnings.map((warning) => (
							<Alert key={warning} showIcon type="warning" message={warning} style={{ marginBottom: 8 }} />
						))}
						<div style={{ marginBottom: 12 }}>
							<RunSummaryLine placeholder run={detailRun} />
						</div>
						{detailRun.result ? (
							<details>
								<summary>Raw result</summary>
								<pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
									{detailRun.result}
								</pre>
							</details>
						) : null}
						{detailRun.logs && !hasStructuredLogs(detailRun) ? (
							<details>
								<summary>Logs</summary>
								<pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, maxHeight: 320, overflow: 'auto' }}>
									{detailRun.logs}
								</pre>
							</details>
						) : null}
					</div>
				) : null}
			</Modal>

			<Modal
				title={
					reportRun
						? `Dry-run report — ${reportRun.companyName} ${reportRun.syncType}`
						: ''
				}
				visible={Boolean(reportRun)}
				width={900}
				footer={<Button onClick={() => setReportRun(null)}>Close</Button>}
				onCancel={() => setReportRun(null)}
			>
				{report ? (
					<div>
						<Alert
							type="info"
							showIcon
							message="No data was written — these are the updates a real run WOULD make."
							style={{ marginBottom: 12 }}
						/>
						{report.result?.summary || report.summary ? (
							<p>
								<Text strong>{report.result?.summary || report.summary}</Text>
							</p>
						) : null}
						{report.fileColumns?.length ? (
							<p>
								<Text type="secondary">File columns: </Text>
								{report.fileColumns.map((column) => (
									<Tag key={column}>{column}</Tag>
								))}
							</p>
						) : null}
						<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
							{Object.entries(report.intendedWrites || {}).length === 0 ? (
								<Tag color="green">No changes — steady state</Tag>
							) : (
								Object.entries(report.intendedWrites || {}).map(([action, count]) => (
									<Tag key={action} color="purple">
										{action}: {count}
									</Tag>
								))
							)}
						</div>
						{(report.sample || []).length ? (
							<Table
								rowKey={(entry, index) => `${entry.action}-${index}`}
								size="small"
								pagination={{ pageSize: 10 }}
								columns={[
									{
										title: 'Action',
										dataIndex: 'action',
										key: 'action',
										render: (value) => (
											<Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
												{value}
											</Text>
										),
									},
									{
										title: 'Detail',
										dataIndex: 'detail',
										key: 'detail',
										render: (value) => (
											<Text code style={{ fontSize: 12 }}>
												{JSON.stringify(value)}
											</Text>
										),
									},
								]}
								dataSource={report.sample}
							/>
						) : null}
					</div>
				) : reportRun ? (
					<Alert type="warning" message="No parseable report on this run." />
				) : null}
			</Modal>

			<Modal
				title={
					bypassRow
						? `Bypass the safety breaker — ${bypassRow.companyName} ${bypassRow.syncType}`
						: ''
				}
				visible={Boolean(bypassRow)}
				width={640}
				okText="Run with breaker bypassed"
				okButtonProps={{
					danger: true,
					disabled:
						(bypassConfirmText || '').trim().toLowerCase() !==
						(bypassRow?.companyName || '').trim().toLowerCase(),
				}}
				onOk={confirmBypass}
				onCancel={() => setBypassRow(null)}
			>
				{bypassRow ? (
					<div>
						<Alert
							showIcon
							type="error"
							message="This re-runs the latest file LIVE with the safety breaker bypassed — for this run only."
							description={breakerBlockReason(bypassRow)}
							style={{ marginBottom: 12 }}
						/>
						<p>
							The breaker exists because a wrong or truncated file can mass-disable a
							workforce or mass-close a job board. Only bypass it if you have verified
							the file is correct and the change is real (e.g. an actual reduction in
							force or board cleanup). If you haven't looked yet, run a dry run first —
							its report shows exactly what this run will write.
						</p>
						<p style={{ marginBottom: 4 }}>
							Type <Text code>{bypassRow.companyName}</Text> to confirm:
						</p>
						<Input
							placeholder={bypassRow.companyName}
							value={bypassConfirmText}
							onChange={(event) => setBypassConfirmText(event.target.value)}
						/>
					</div>
				) : null}
			</Modal>

			<Modal
				title="Dead-letter queue — runs stuck after retries"
				visible={dlqOpen}
				width={860}
				footer={
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Button icon={<ReloadOutlined />} onClick={refreshDlqList}>
							Re-check queues
						</Button>
						<Button onClick={() => setDlqOpen(false)}>Close</Button>
					</div>
				}
				onCancel={() => setDlqOpen(false)}
			>
				{dlqMessages === null ? (
					<Spinner />
				) : dlqMessages.length === 0 ? (
					<Alert showIcon type="success" message="All pipeline DLQs are empty." />
				) : (
					<div>
						<Alert
							showIcon
							type="info"
							message="These runs exhausted their SQS retries. Their ledger rows failed at the time, but the feed may have recovered since — check each feed's history."
							style={{ marginBottom: 12 }}
						/>
						<Table
							rowKey={(entry, index) => `${entry.ledgerId || index}`}
							size="small"
							pagination={false}
							dataSource={dlqMessages}
							columns={[
								{
									title: 'Company',
									key: 'company',
									render: (entry) => (
										<Text strong>{entry.companyName || entry.companyId || '—'}</Text>
									),
								},
								{
									title: 'Task',
									dataIndex: 'queueTaskType',
									key: 'task',
									width: 100,
									render: (value) => <Tag color="blue">{value}</Tag>,
								},
								{
									title: 'File',
									key: 'file',
									render: (entry) => (
										<Tooltip title={entry.key}>
											<Text style={{ maxWidth: 220 }} ellipsis>
												{shortFile(entry.key)}
											</Text>
										</Tooltip>
									),
								},
								{
									title: 'Died',
									key: 'sent',
									width: 110,
									render: (entry) => (entry.sentAt ? formatAgo(entry.sentAt) : '—'),
								},
								{
									title: 'Attempts',
									dataIndex: 'receiveCount',
									key: 'attempts',
									width: 90,
								},
								{
									title: '',
									key: 'actions',
									width: 250,
									render: (entry) => (
										<div style={{ display: 'flex', gap: 6 }}>
											{entry.companyId && entry.taskType ? (
												<Button
													size="small"
													onClick={() => {
														setDlqOpen(false);
														setDrawerKey(`SFTP|${entry.companyId}|${entry.taskType}|`);
													}}
												>
													History
												</Button>
											) : null}
											{entry.ledgerId ? (
												<Popconfirm
													title="Re-queue this run? If a newer file has completed since, the worker safely skips it (stale-replay guard). Otherwise it runs LIVE and writes data. Retrying an older file than the latest delivery asks for extra confirmation."
													okText="Retry"
													onConfirm={() => retryDlqEntry(entry)}
												>
													<Button size="small" type="primary">
														Retry
													</Button>
												</Popconfirm>
											) : null}
											{entry.ledgerId ? (
												<Popconfirm
													title="Remove this message from the DLQ? The run will NOT be retried."
													okText="Remove"
													okButtonProps={{ danger: true }}
													onConfirm={() =>
														actOnDlqEntry(entry, deleteDlqMessage, 'Removed')
													}
												>
													<Button danger size="small">
														Remove
													</Button>
												</Popconfirm>
											) : null}
										</div>
									),
								},
							]}
						/>
					</div>
				)}
			</Modal>

			<MappingEditorDrawer
				row={mappingRow}
				currentUser={currentUser}
				onClose={() => setMappingRow(null)}
				onTestRun={(candidateDoc) =>
					triggerSftpRow(mappingRow, 'dryrun-pending', 'Candidate-doc dry run', candidateDoc)
				}
			/>

			<AefMappingWizard
				visible={wizardOpen}
				onClose={() => setWizardOpen(false)}
				client={client}
				onDryRun={wizardDryRun}
				updatedByEmail={currentUser?.emailAddress}
				companies={companyOptions}
				defaultCompanyId={currentUser?.companyId || currentUser?.company?.id || ''}
				defaultCompanyName={currentUser?.company?.name || ''}
			/>
		</main>
	);
};

export const AdminIntegrations = withApollo(AdminIntegrationsComponent);
